import { useEffect, useState, useCallback } from "react";
import apiWithRefresh from "../utils/apiWithRefresh";
import useDarkMode from "./useDarkMode";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Typography,
  IconButton,
  Stack,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
//import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SyncIcon from "@mui/icons-material/Sync";
import { useToast } from "../utils/useToast";
import GlobalToast from "../components/GlobalToast";
import { Helmet } from "react-helmet-async";

const initialForm = {
  id: null,
  feePlanId: "",
  countryCurrencyId: "",
  amount: "",
};

const AdminFeePlanPrices = () => {
  // Manual sync handler (must be after showToast and fetchAll are defined)
  const handleManualSync = async (id) => {
    try {
      showToast("Syncing with Razorpay...", "info");
      await apiWithRefresh.post(`fee-plan-prices/${id}/sync-razorpay-plan`);
      showToast("Razorpay plan synced!", "success");
      fetchAll();
    } catch (err) {
      showToast(err.message || "Sync failed", "error");
    }
  };
  useDarkMode();
  const [prices, setPrices] = useState([]);
  const [feePlans, setFeePlans] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(initialForm);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, showToast] = useToast();
  const [selectedCountryCurrencyId, setSelectedCountryCurrencyId] =
    useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pricesRes, feePlansRes, countriesRes] = await Promise.all([
        apiWithRefresh.get("fee-plan-prices"),
        apiWithRefresh.get("fee-plans"),
        apiWithRefresh.get("countrycurrency"),
      ]);
      const rows = pricesRes.map((item) => ({
        ...item,
        currencyCode: item.countryCurrency?.code || "",
        razorpayPlanId: item.razorpay_plan_id,
      }));
      console.log("Fetched FeePlanPrices:", rows);
      setPrices(rows);
      setFeePlans(feePlansRes);
      setCountries(countriesRes);
    } catch (err) {
      // error handling can be added here if needed
      // setError(err.message); // removed to fix no-undef
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (countries.length > 0 && !selectedCountryCurrencyId) {
      setSelectedCountryCurrencyId(countries[0].id);
    }
  }, [countries, selectedCountryCurrencyId]);

  const openAddModal = () => {
    setForm(initialForm);
    setEditingId(null);
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setForm({
      id: item.id,
      feePlanId: item.feePlanId,
      countryCurrencyId: item.countryCurrencyId,
      amount: item.amount,
    });
    setEditingId(item.id);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(initialForm);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleAddOrUpdate = async (e) => {
    e.preventDefault();
    if (!form.feePlanId || !form.countryCurrencyId || !form.amount) {
      showToast("All fields are required.", "warning");
      return;
    }
    try {
      const payload = {
        feePlanId: form.feePlanId,
        countryCurrencyId: form.countryCurrencyId,
        amount: form.amount,
      };
      if (editingId) {
        await apiWithRefresh.put(`fee-plan-prices/${editingId}`, payload);
        showToast("FeePlanPrice updated successfully!", "success");
      } else {
        await apiWithRefresh.post("fee-plan-prices", payload);
        showToast("FeePlanPrice added successfully!", "success");
      }
      setModalOpen(false);
      setEditingId(null);
      setForm(initialForm);
      fetchAll();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const openDeleteConfirm = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      await apiWithRefresh.delete(`fee-plan-prices/${deleteId}`);
      showToast("FeePlanPrice deleted successfully!", "success");
      setConfirmOpen(false);
      setDeleteId(null);
      fetchAll();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const [revealedPlanIds, setRevealedPlanIds] = useState({});
  const handleTogglePlanId = (id) => {
    setRevealedPlanIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const columns = [
    {
      field: "feePlan",
      headerName: "Fee Plan",
      flex: 1,
      valueGetter: (params) => {
        return params.name || params.row.name || "";
      },
    },
    { field: "amount", headerName: "Monthly Fee", flex: 1 },
    { field: "currencyCode", headerName: "Currency", flex: 1 },
    {
      field: "razorpayPlanId",
      headerName: "Razorpay Plan ID",
      flex: 1,
      renderCell: (params) => {
        const id = params.row.id;
        const planId = params.row.razorpayPlanId;
        const isSynced = !!planId;
        if (revealedPlanIds[id]) {
          return (
            <Button
              variant="text"
              size="small"
              onClick={() => handleTogglePlanId(id)}
              style={{ textTransform: "none", padding: 0, minWidth: 0 }}
            >
              {planId || <span style={{ color: "#aaa" }}>N/A</span>}
              {!isSynced && (
                <span style={{ color: "#e57373", marginLeft: 8 }}>
                  (Not Synced)
                </span>
              )}
            </Button>
          );
        }
        return (
          <Button
            variant="text"
            size="small"
            onClick={() => handleTogglePlanId(id)}
            style={{ textTransform: "none", padding: 0, minWidth: 0 }}
          >
            {isSynced ? (
              "Show Plan ID"
            ) : (
              <span style={{ color: "#e57373" }}>Not Synced</span>
            )}
          </Button>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton color="primary" onClick={() => openEditModal(params.row)}>
            <EditIcon />
          </IconButton>
          <IconButton
            color="secondary"
            onClick={() => handleManualSync(params.row.id)}
            title="Sync Razorpay Plan"
          >
            <SyncIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => openDeleteConfirm(params.row.id)}
          >
            {/* <DeleteIcon /> */}
          </IconButton>
        </Stack>
      ),
      width: 160,
    },
  ];

  return (
    <>
      <Helmet>
        <title>AKAAL KIDS - Admin Panel | Fee Plan Prices</title>
      </Helmet>
      <Box sx={{ p: 2 }}>
        <GlobalToast toast={toast} />
        <Typography variant="h5" gutterBottom>
          Fee Plan Prices
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Select
            value={selectedCountryCurrencyId}
            onChange={(e) => setSelectedCountryCurrencyId(e.target.value)}
            displayEmpty
            sx={{ minWidth: 220, mr: 2 }}
          >
            {countries.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.country} ({c.code})
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openAddModal}
            sx={{ mr: 2 }}
          >
            Add Price
          </Button>
        </Box>
        <div className="responsiveTable" style={{ height: 500 }}>
          <DataGrid
            rows={
              selectedCountryCurrencyId
                ? prices.filter(
                    (p) => p.countryCurrencyId === selectedCountryCurrencyId,
                  )
                : prices
            }
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            loading={loading}
            disableSelectionOnClick
            getRowId={(row) => row.id}
          />
        </div>
        <Dialog
          open={modalOpen}
          onClose={handleModalClose}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{editingId ? "Edit Price" : "Add Price"}</DialogTitle>
          <form onSubmit={handleAddOrUpdate}>
            <DialogContent>
              <Select
                name="feePlanId"
                value={form.feePlanId}
                onChange={handleChange}
                fullWidth
                required
                displayEmpty
                sx={{ mb: 2 }}
              >
                <MenuItem value="">Select Fee Plan</MenuItem>
                {feePlans.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
              <Select
                name="countryCurrencyId"
                value={form.countryCurrencyId}
                onChange={handleChange}
                fullWidth
                required
                displayEmpty
                sx={{ mb: 2 }}
              >
                <MenuItem value="">Select Country</MenuItem>
                {countries.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.country} ({c.code})
                  </MenuItem>
                ))}
              </Select>
              <TextField
                name="amount"
                label="Amount"
                type="number"
                value={form.amount}
                onChange={handleChange}
                fullWidth
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleModalClose}>Cancel</Button>
              <Button type="submit" variant="contained" color="primary">
                {editingId ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Alert severity="warning">
              Are you sure you want to delete this price?
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default AdminFeePlanPrices;
